import { Card } from "@verde/compose-ui";
import {
  useFetchClientCreditUsage,
  useFetchClientDataConsent,
  useFetchCreditUsage,
} from "@verde/entities";
import { Skeleton } from "@verde/ui-core";
import {
  AuthorizationDialog,
  CreditDialog,
  CreditUsageDialog,
} from "modules/credit-profile/components";
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import PanelAction from "./panel-action";
import PanelHasClient from "./panel-has-client";
import PanelSearchClient from "./panel-search-client";
import type {
  CreditProfilePanelProps,
  Tab,
  UseCreditProfilePanel,
} from "./types";

export const TABS: Array<Tab> = [
  {
    id: "resume",
    title: "Resumo",
  },
  {
    id: "debts",
    title: "Endividamento",
  },
  {
    id: "serasa",
    title: "Serasa",
  },
  {
    id: "properties",
    title: "Propriedades",
    person_kind: ["PERSON"],
  },
  {
    id: "partners",
    title: "Sócios",
    person_kind: ["COMPANY"],
    hide: process.env.REACT_APP_ENVIRONMENT !== "staging",
  },
];

export const useCreditProfilePanel = create<UseCreditProfilePanel>((set) => {
  return {
    client: undefined,
    resource: {
      authorized: false,
      available: false,
    },
    currentTab: TABS?.find((tab) => !tab.hide)?.id || TABS[0].id,
    creditsUsage: {
      open: false,
      mode: undefined,
    },
    updating: false,
    handleUnlock: () => undefined,
    setClient: (client) => {
      set(() => ({ client }));
    },
    dispatchCreditsUsage: ({ type }) => {
      set(() => ({
        creditsUsage: { open: type !== "close", mode: type },
      }));
    },
    setCurrentTab: (tab) => {
      set(() => ({ currentTab: tab }));
    },
  };
});

function CreditProfilePanel({
  client: default_client,
  mode,
  loading,
  options,
  credit_usage_id,
  onSearchClient,
  onResource,
}: CreditProfilePanelProps) {
  const navigate = useNavigate();

  const { client, setClient, creditsUsage, dispatchCreditsUsage } =
    useCreditProfilePanel();

  const [isAuthorizationOpen, toggleAuthorizationDialog] = useReducer(
    (state) => !state,
    false,
  );

  const [buyOpen, toggleBuy] = useReducer((state) => !state, false);

  const client_credit_usage_fetch = useFetchClientCreditUsage(client?.id, {
    enabled: !credit_usage_id && !!client?.id,
  });
  const credit_usage_fetch = useFetchCreditUsage(credit_usage_id, {
    enabled: !!credit_usage_id,
  });

  const credit_usage = credit_usage_id
    ? credit_usage_fetch
    : client_credit_usage_fetch;
  const data_consent = useFetchClientDataConsent(client?.id);

  const isLoading = loading || credit_usage.isLoading || data_consent.isLoading;

  const consentment = useMemo(() => {
    if (data_consent.data?.kind !== "scr") return;

    return data_consent.data;
  }, [data_consent.data]);

  const resource = useMemo(() => {
    const authorized = consentment?.status === "approved";

    return {
      authorized,
      available:
        authorized && (credit_usage.data?.updating || !!credit_usage.data?.id),
      data: credit_usage.data,
    };
  }, [consentment, credit_usage.data]);

  const updating = useMemo(() => {
    return !!credit_usage.data?.updating;
  }, [credit_usage.data]);

  const handleUnlock = useCallback(() => {
    if (mode === "anchor") {
      navigate(`/clientes/${client?.id}`);
      return;
    }

    if (resource.authorized) {
      dispatchCreditsUsage({
        type: "open",
      });

      return;
    }

    toggleAuthorizationDialog();
  }, [mode, navigate, resource.authorized, client, dispatchCreditsUsage]);

  useEffect(() => {
    setClient(default_client);
  }, [default_client, setClient]);

  useEffect(() => {
    useCreditProfilePanel.setState(() => {
      return {
        mode,
        resource,
        updating,
        options,
        consentment,
        handleUnlock,
        onSearchClient,
      };
    });
  }, [
    mode,
    resource,
    consentment,
    options,
    updating,
    handleUnlock,
    onSearchClient,
  ]);

  useEffect(() => {
    if (onResource) {
      onResource(resource);
    }
  }, [resource, onResource]);

  return (
    <>
      <Card
        classNames={{
          icon: "w-6 h-6",
          title: "text-brand-green-dark text-2xl",
          content: "gap-0",
        }}
        title={
          <img
            alt="Logotipo Raio-X rural"
            src="/static/brand/raio-x-rural.svg"
            className="h-5"
          />
        }
        description="Consulte dados cadastrais, contratos de financiamento e histórico de clientes (pessoas físicas ou jurídicas) para avaliar seu perfil financeiro e nível de endividamento. Informações disponíveis em fontes como CERC, BNDS, SCR, Serasa e Boa Vista."
        action={
          mode && ["anchor", "fund-manager"].includes(mode)
            ? undefined
            : {
                render: <PanelAction />,
              }
        }
      >
        {isLoading && (
          <div className="grid gap-1">
            <Skeleton height={100} />
            <Skeleton height={240} />
          </div>
        )}

        {!isLoading && (
          <div className="grid gap-4">
            {(!client?.id || mode === "standalone") && <PanelSearchClient />}
            {!!client?.id && <PanelHasClient />}
          </div>
        )}
      </Card>

      {isAuthorizationOpen && (
        <AuthorizationDialog
          client={client}
          onClose={toggleAuthorizationDialog}
        />
      )}

      {creditsUsage.open && (
        <CreditUsageDialog
          client={client}
          onBuy={toggleBuy}
          onClose={() => {
            dispatchCreditsUsage({
              type: "close",
            });
          }}
          refresh={creditsUsage.mode === "refresh"}
          creditUsageId={resource.data?.id}
        />
      )}

      {buyOpen && <CreditDialog onClose={toggleBuy} />}
    </>
  );
}

export default React.memo(CreditProfilePanel);
